import type { Property } from '~/core/entities/property';
import type { SectionName } from '~/types/mixpanel';
import type { VariantProps } from 'class-variance-authority';
import type { PrimitiveProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';

import { cva } from 'class-variance-authority';

export { default as PropertyCard } from './Card.vue';
export { default as PropertyCardFooter } from './PropertyCardFooter.vue';
export { default as PropertyCardHeader } from './PropertyCardHeader.vue';
export { default as PropertyCardItemSeparator } from './PropertyCardItemSeparator.vue';
export { default as PropertyCardContent } from './PropertyCardContent.vue';
export { default as PropertyCardSkeleton } from './PropertyCardSkeleton.vue';
export { default as SimilarPropertiesModal } from './SimilarPropertiesModal.vue';
export { default as SimilarPropertiesList } from './SimilarPropertiesList.vue';

export interface PropertyCardProps {
	property: Property;
	section?: SectionName;
	cardPosition?: number;
	showActions?: boolean;
	size?: PropertyCardBodyVariants['size'];
	asTags?: PropertyCardFooterProps['asTags'];
	variant?: PropertyCardBodyVariants['variant'];
	class?: HTMLAttributes['class'];
	disableLink?: boolean;
}

export interface PropertyCardHeaderProps {
	class?: HTMLAttributes['class'];
	property: Property;
	isMouseOverCardHeader?: boolean;
	size: PropertyCardProps['size'];
	variant: PropertyCardProps['variant'];
	cardPosition?: number;
	section?: SectionName;
	isPropertyActive?: boolean;
	dotClasses?: string;
}

export interface PropertyCardContentProps {
	class?: HTMLAttributes['class'];
	variant: PropertyCardProps['variant'];
	property: Property;
}
export interface PropertyCardFooterProps {
	class?: HTMLAttributes['class'];
	size?: PropertyCardBodyVariants['size'];
	variant: PropertyCardProps['variant'];
	property: Property;
	extendedHeight?: boolean;
	showActions?: boolean;
	isPropertyActive?: boolean;
	asTags?: {
		askingPrice: PrimitiveProps['as'];
		addresses: PrimitiveProps['as'];
		infoItems: PrimitiveProps['as'];
	};
}

export interface PropertyCardSkeletonProps {
	variant: PropertyCardBodyVariants['variant'];
	size?: PropertyCardBodyVariants['size'];
}

export const propertyCardBodyVariants = cva('relative w-full', {
	variants: {
		variant: {
			whiteBackground: 'border border-white-dark-3',
			transparentBackground: 'border border-primary-light-3',
			transparentBackgroundWithoutBorder: 'border border-transparent',
			fullFace: 'size-full'
		},
		size: {
			sm: '',
			md: '',
			lg: ''
		}
	},
	defaultVariants: {
		variant: 'whiteBackground',
		size: 'sm'
	}
});

export const propertyCardContentVariants = cva(
	'absolute bottom-0 z-20 flex h-[62px] w-full items-center bg-primary/40 p-4 py-3',
	{
		variants: {
			variant: {
				whiteBackground: '',
				transparentBackground: '',
				transparentBackgroundWithoutBorder: '',
				fullFace:
					'absolute bottom-0 z-30 w-full items-end justify-start border-none bg-transparent xl:justify-end'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground'
		}
	}
);

export const propertyCardBrokerVariants = cva(
	'w-full max-w-[calc(100%-80px)]',
	{
		variants: {
			variant: {
				whiteBackground: '',
				transparentBackground: '',
				transparentBackgroundWithoutBorder: '',
				fullFace:
					'max-w-[80%] xl:min-w-0 xl:max-w-[45%] xl:flex-1 xl:justify-end'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground'
		}
	}
);

export const propertyCardFooterBodyVariants = cva(
	'relative  w-full flex-col p-4',
	{
		variants: {
			variant: {
				whiteBackground: 'bg-white',
				transparentBackground: 'bg-black',
				transparentBackgroundWithoutBorder: 'bg-black',
				fullFace:
					'absolute bottom-0 z-20 flex !h-[165px] bg-black/40 first:pb-40 xl:!h-[120px]'
			},
			size: {
				sm: 'h-[120px]',
				md: 'h-[120px] min-h-[111px]',
				lg: 'h-[160px]'
			},
			extendedHeight: {
				true: 'min-h-[150px] md:min-h-[150px] lg:max-h-[190px]'
			}
		},
		compoundVariants: [
			{
				variant: 'fullFace',
				extendedHeight: true,
				class: '!h-[208px] md:!h-[205px]  lg:!min-h-[150px]'
			}
		],
		defaultVariants: {
			variant: 'whiteBackground',
			size: 'sm'
		}
	}
);

export const propertyCardFooterPriceVariants = cva(
	'font-matter-sq font-normal tracking-tight',
	{
		variants: {
			variant: {
				whiteBackground: 'text-primary',
				transparentBackground: 'text-white',
				transparentBackgroundWithoutBorder: 'text-white',
				fullFace: 'text-white'
			},
			size: {
				sm: 'text-[18px] leading-[27px]',
				md: 'text-[18px] md:leading-[27px]',
				lg: 'text-[18px] md:text-[24px] md:leading-[27px]'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground',
			size: 'sm'
		}
	}
);

export const propertyCardFooterTextsVariants = cva('font-medium ', {
	variants: {
		variant: {
			whiteBackground: 'text-primary-light-3',
			transparentBackground: 'text-white',
			transparentBackgroundWithoutBorder: 'text-white',
			fullFace: 'text-white'
		},
		size: {
			sm: 'text-[12px] leading-[18px]',
			md: 'text-[12px] leading-[18px]  ',
			lg: 'text-[12px] leading-[18px] md:text-[14px] md:leading-[20px]'
		}
	},
	defaultVariants: {
		variant: 'whiteBackground',
		size: 'sm'
	}
});

export const propertyCardFooterAddressVariants = cva('text-left font-medium', {
	variants: {
		variant: {
			whiteBackground: 'text-primary-light-2',
			transparentBackground: 'text-white-dark-3',
			transparentBackgroundWithoutBorder: 'text-white-dark-3',
			fullFace: 'text-white-dark-3'
		},
		size: {
			sm: 'text-[12px] leading-[18px]',
			md: 'text-[12px] leading-[18px]',
			lg: 'text-[12px] leading-[18px]'
		}
	},
	defaultVariants: {
		variant: 'whiteBackground',
		size: 'sm'
	}
});

export const propertyCardBadgePoiVariants = cva(
	'my-2 flex flex-row items-center',
	{
		variants: {
			variant: {
				whiteBackground: '',
				transparentBackground: '',
				transparentBackgroundWithoutBorder: '',
				fullFace: 'mb-5'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground'
		}
	}
);

export const propertyCardCommercialIdVariants = cva(
	'text-xs font-medium leading-[18px]',
	{
		variants: {
			variant: {
				whiteBackground: 'text-primary-light-1',
				transparentBackground: 'text-white-dark-4',
				transparentBackgroundWithoutBorder: 'text-white-dark-4',
				fullFace: 'text-white-dark-4'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground'
		}
	}
);

export const propertyCardHeaderBodyVariants = cva(
	'relative w-full overflow-hidden p-0',
	{
		variants: {
			variant: {
				fullFace: '!size-full',
				whiteBackground: '',
				transparentBackground: '',
				transparentBackgroundWithoutBorder: ''
			},
			size: {
				sm: 'h-[220px] md:h-[256px]',
				md: 'h-[220px] md:h-[256px] md:min-h-[256px]',
				lg: 'h-[365px]'
			}
		},
		defaultVariants: {
			variant: 'whiteBackground',
			size: 'md'
		}
	}
);

export const propertyCardSkeletonVariants = cva('', {
	variants: {
		variant: {
			fullFace: 'border-primary-light-4',
			whiteBackground: 'border-white-dark-2',
			transparentBackground: 'border-primary-light-4',
			transparentBackgroundWithoutBorder: 'border-primary-light-4'
		}
	}
});

export const propertyCardCarouselDotsVariants = cva(
	'absolute inset-x-0 mx-auto flex h-5 w-fit gap-2 rounded-full bg-primary/40 px-3 py-2  transition-opacity delay-300 ease-in-out',
	{
		variants: {
			size: {
				baseline: 'bottom-[25px]',
				sm: 'bottom-[67px]',
				md: 'bottom-[67px] md:bottom-[84px]',
				lg: 'bottom-[172px] xl:bottom-[126px]'
			},
			extendedBottom: {
				true: ''
			},
			variant: {
				fullFace: 'opacity-100'
			}
		},
		compoundVariants: [
			{
				extendedBottom: true,
				class: 'bottom-[70px]'
			},
			{
				size: 'lg',
				extendedBottom: true,
				class: 'bottom-[215px] lg:bottom-[200px] xl:bottom-[160px]'
			}
		],
		defaultVariants: {
			size: 'sm'
		}
	}
);

export type PropertyCardCarouselDotsVariants = VariantProps<
	typeof propertyCardCarouselDotsVariants
>;

export type PropertyCardBodyVariants = VariantProps<
	typeof propertyCardBodyVariants
>;

export type PropertyCardFooterBodyVariants = VariantProps<
	typeof propertyCardFooterBodyVariants
>;
export type PropertyCardFooterPriceVariants = VariantProps<
	typeof propertyCardFooterPriceVariants
>;
export type PropertyCardFooterTextsVariants = VariantProps<
	typeof propertyCardFooterTextsVariants
>;
export type PropertyCardFooterAddressVariants = VariantProps<
	typeof propertyCardFooterAddressVariants
>;
export type PropertyCardFooterCommercialIdVariants = VariantProps<
	typeof propertyCardCommercialIdVariants
>;
export type PropertyCardHeaderBodyVariants = VariantProps<
	typeof propertyCardHeaderBodyVariants
>;
